import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { TwoFactorChallenge } from '@/components/auth/TwoFactorChallenge'
import { useAuth, useAutofillSync } from '@/hooks'
import { toast } from '@/hooks/use-toast'
import { getErrorMessage } from '@/utils/error'
import { isFCamaraEmail } from '@/utils/fcamara'

const Auth = () => {
  const {
    login,
    isLoggingIn,
    twoFactorChallenge,
    verifyTwoFactor,
    isVerifyingTwoFactor,
    resendTwoFactor,
    cancelTwoFactor,
    emailNotVerified,
    resendVerification,
    clearEmailNotVerified,
  } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isResending, setIsResending] = useState(false)

  // Contas FCamara entram só pelo Microsoft 365 — o campo de senha some e o
  // botão 365 aparece quando o e-mail digitado é de domínio FCamara.
  // Autofill/gerenciador de senha preenche sem disparar onChange; o ref
  // sincroniza o valor real do DOM pra detecção reagir mesmo assim.
  const emailRef = useAutofillSync(email, setEmail)
  const emailIsFCamara = isFCamaraEmail(email)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Segurança: e-mail FCamara não loga por senha (o back também rejeita).
    if (emailIsFCamara) return
    login({
      email,
      password,
    })
  }

  const handleResendVerification = async () => {
    setIsResending(true)
    try {
      const res = await resendVerification(email)
      toast({ title: 'E-mail reenviado!', description: res.message })
    } catch (error: unknown) {
      toast({
        title: 'Erro ao reenviar',
        description: getErrorMessage(error, 'Tente novamente em instantes.'),
        variant: 'destructive',
      })
    } finally {
      setIsResending(false)
    }
  }

  const handleOutlookLogin = () => {
    // Redireciona para o endpoint OAuth do backend
    window.location.href = `${import.meta.env.VITE_API_URL}/auth/microsoft`
  }

  const isLoading = isLoggingIn

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent/5 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.5, 0.3, 0.5],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-sm relative z-10"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-primary tracking-wider">AI SYM</h1>
          <p className="text-3xl font-bold text-primary mt-2">Welcome</p>
        </div>

        {twoFactorChallenge ? (
          <TwoFactorChallenge
            method={twoFactorChallenge.method}
            isVerifying={isVerifyingTwoFactor}
            onVerify={verifyTwoFactor}
            onResend={resendTwoFactor}
            onCancel={cancelTwoFactor}
          />
        ) : (
          <>
            {/* Email — sempre visível; define se o acesso é por senha ou SSO 365 */}
            <div className="space-y-2 mb-4">
              <Label htmlFor="email" className="text-foreground">
                Email
              </Label>
              <Input
                ref={emailRef}
                id="email"
                type="email"
                placeholder="Digite seu email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (emailNotVerified) clearEmailNotVerified()
                }}
                className="h-12 bg-muted/30 border-border focus:border-primary"
                required
                disabled={isLoading}
              />
            </div>

            {emailIsFCamara ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Conta FCamara: o acesso é feito pelo Microsoft 365.
                </p>
                {/* Microsoft 365 Button */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 border-primary text-foreground hover:bg-primary/10 flex items-center justify-center gap-3"
                  onClick={handleOutlookLogin}
                  disabled={isLoading}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5">
                    <path
                      fill="#0078D4"
                      d="M24 7.387v10.478c0 .23-.08.424-.238.576a.806.806 0 0 1-.587.234h-8.653v-6.18l1.027.756a.349.349 0 0 0 .192.074.324.324 0 0 0 .192-.074l7.905-5.86a.36.36 0 0 1 .162 0z"
                    />
                    <path
                      fill="#0078D4"
                      d="m24 5.39-8.816 6.548a.612.612 0 0 1-.36.119.612.612 0 0 1-.36-.119L5.649 5.39a.456.456 0 0 1 .11-.339.387.387 0 0 1 .317-.163h17.099c.23 0 .418.076.563.227.145.15.218.334.218.55a.439.439 0 0 1-.044-.181c0-.044.03-.07.088-.094z"
                    />
                    <path
                      fill="#0078D4"
                      d="M8.857 17.5H0V7.386c0-.318.122-.59.365-.817.244-.228.537-.341.88-.341h7.612v11.272z"
                    />
                    <path
                      fill="#0364B8"
                      d="M8.857 6.228v11.648a.82.82 0 0 1-.055.303.73.73 0 0 1-.438.445.786.786 0 0 1-.302.053H0V6.847c0-.34.114-.629.341-.866.227-.236.515-.354.863-.354h6.886c.226 0 .42.08.58.238.16.16.24.353.24.58-.02-.116-.033-.184-.053-.217z"
                    />
                    <path
                      fill="#0078D4"
                      d="m8.857 6.228.04.22c.01.05.013.08.013.126v11.302a.786.786 0 0 1-.053.302.73.73 0 0 1-.438.446.786.786 0 0 1-.302.053H0V6.228h8.857z"
                    />
                    <path
                      fill="#28A8EA"
                      d="M8.91 17.5H0V6.228h8.857v.376h.053c-.035.097-.053.189-.053.275v10.07c0 .193.035.367.053.55z"
                    />
                    <path
                      fill="#0078D4"
                      d="M14.522 12.495v6.18h-5.45a.82.82 0 0 1-.303-.054.73.73 0 0 1-.445-.445.786.786 0 0 1-.054-.302V6.994c0-.107.018-.21.054-.306a.73.73 0 0 1 .445-.446.786.786 0 0 1 .302-.053h5.451v6.306z"
                    />
                    <path
                      fill="#50D9FF"
                      d="M14.522 6.19v5.9l-.022.405h-5.428a.786.786 0 0 1-.302-.053.73.73 0 0 1-.446-.446.82.82 0 0 1-.054-.302V6.994c0-.107.018-.21.054-.306a.73.73 0 0 1 .446-.446.786.786 0 0 1 .302-.053h5.45z"
                    />
                  </svg>
                  Continue with Outlook 365
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {emailNotVerified && (
                  <Alert className="border-primary/30 bg-primary/5">
                    <AlertTitle>Confirme seu e-mail</AlertTitle>
                    <AlertDescription className="space-y-3">
                      <p>
                        Sua conta ainda não foi ativada. Clique no link que enviamos para o seu
                        e-mail — ou reenvie abaixo.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={handleResendVerification}
                        disabled={isResending}
                      >
                        {isResending ? 'Reenviando...' : 'Reenviar e-mail de ativação'}
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-foreground">
                    Senha
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Digite sua senha"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 bg-muted/30 border-border focus:border-primary pr-12"
                      required
                      disabled={isLoading}
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      disabled={isLoading}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="text-right">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Esqueceu sua senha?
                  </Link>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium mt-6"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Entrando...
                    </>
                  ) : (
                    <>Entrar</>
                  )}
                </Button>
              </form>
            )}

            {!emailIsFCamara && (
              <p className="text-center text-muted-foreground mt-6">
                Não tem uma conta?{' '}
                <Link to="/signup" className="text-primary hover:underline font-medium">
                  Criar conta
                </Link>
              </p>
            )}
          </>
        )}
      </motion.div>
    </div>
  )
}

export default Auth
